import { analyze } from 'web-audio-beat-detector';

export interface AudioMixOptions {
  voiceoverVolume: number;
  musicVolume: number;
  duckingAmount: number;
}

export class AudioEngine {
  private static context: AudioContext | null = null;

  static getContext() {
    if (!this.context) {
      this.context = new AudioContext();
    }
    return this.context;
  }

  static async loadAudio(url: string): Promise<AudioBuffer> {
    const ctx = this.getContext();
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await ctx.decodeAudioData(arrayBuffer);
  }

  static async detectBeats(buffer: AudioBuffer): Promise<number[]> {
    try {
      const tempo = await analyze(buffer);
      const beatDuration = 60 / tempo;
      const targetDuration = 12;
      const beatsPerPhrase = Math.max(8, Math.round((targetDuration / beatDuration) / 4) * 4);
      const phraseDuration = beatDuration * beatsPerPhrase;

      const beats: number[] = [];
      for (let t = 0; t < buffer.duration; t += phraseDuration) {
        if (buffer.duration - t < phraseDuration * 0.5 && beats.length > 0) break;
        beats.push(t);
      }
      return beats;
    } catch (e) {
      console.error('Beat detection failed:', e);
      return [];
    }
  }

  static async mixAudio(
    voiceover: AudioBuffer | null,
    music: AudioBuffer | null,
    options: AudioMixOptions,
    totalDuration: number
  ): Promise<AudioBuffer> {
    if (totalDuration <= 0) {
      // Return a tiny silent buffer
      const ctx = this.getContext();
      return ctx.createBuffer(1, 1, ctx.sampleRate);
    }

    const sampleRate = 44100;
    const offlineCtx = new OfflineAudioContext(2, totalDuration * sampleRate, sampleRate);

    // Music Source
    let musicNode: AudioBufferSourceNode | null = null;
    let musicGain: GainNode | null = null;

    if (music) {
      musicNode = offlineCtx.createBufferSource();
      musicNode.buffer = music;
      musicNode.loop = true; // Loop music to fill entire duration
      musicGain = offlineCtx.createGain();
      musicGain.gain.value = options.musicVolume;
      musicNode.connect(musicGain);
      musicGain.connect(offlineCtx.destination);
    }

    const startDelay = 2.0;

    // Voiceover Source
    if (voiceover) {
      const voNode = offlineCtx.createBufferSource();
      voNode.buffer = voiceover;
      const voGain = offlineCtx.createGain();
      voGain.gain.value = options.voiceoverVolume;
      voNode.connect(voGain);
      voGain.connect(offlineCtx.destination);

      // Implement Ducking with the startDelay offset
      if (musicGain) {
        this.applyDucking(offlineCtx, voiceover, musicGain, options, startDelay);
      }

      voNode.start(startDelay);
    }

    if (musicNode) {
      musicNode.start(0);

      // Fade out at end (like useComposition)
      if (voiceover) {
        const fadeStart = startDelay + voiceover.duration;
        const fadeEnd = Math.min(fadeStart + 2.0, totalDuration);
        
        musicGain!.gain.setValueAtTime(options.musicVolume, fadeStart);
        musicGain!.gain.linearRampToValueAtTime(0, fadeEnd);
      }
    }

    return await offlineCtx.startRendering();
  }

  private static applyDucking(
    ctx: OfflineAudioContext,
    voiceover: AudioBuffer,
    musicGain: GainNode,
    options: AudioMixOptions,
    startOffset: number
  ) {
    const { duckingAmount, musicVolume } = options;
    if (duckingAmount <= 0) return;

    const channelData = voiceover.getChannelData(0);
    const sampleRate = voiceover.sampleRate;
    
    const windowDuration = 0.05; // 50ms windows for high resolution
    const windowSize = Math.floor(sampleRate * windowDuration);
    const numWindows = Math.ceil(channelData.length / windowSize);
    const threshold = 0.05;

    // 1. Detect raw voice presence
    const rawEnvelope = new Float32Array(numWindows);
    for (let i = 0; i < numWindows; i++) {
      const offset = i * windowSize;
      let maxAmp = 0;
      for (let j = 0; j < windowSize && offset + j < channelData.length; j++) {
        maxAmp = Math.max(maxAmp, Math.abs(channelData[offset + j]!));
      }
      rawEnvelope[i] = maxAmp > threshold ? 1 : 0;
    }

    // 2. Apply Hold time (prevent jitter between words)
    // 1.0 seconds = 20 windows of 50ms
    const holdWindows = Math.ceil(1.0 / windowDuration);
    const heldEnvelope = new Float32Array(numWindows);
    let holdCounter = 0;
    
    for (let i = 0; i < numWindows; i++) {
      if (rawEnvelope[i] === 1) {
        holdCounter = holdWindows;
      }
      heldEnvelope[i] = holdCounter > 0 ? 1 : 0;
      if (holdCounter > 0) holdCounter--;
    }

    // 3. Apply Smoothing (Attack and Release)
    const smoothedEnvelope = new Float32Array(numWindows);
    let currentVal = 0;
    const attackAlpha = 0.8;  // Fast ducking when voice starts
    const releaseAlpha = 0.05; // Slow, graceful volume return
    
    for (let i = 0; i < numWindows; i++) {
      const target = heldEnvelope[i]!;
      if (target > currentVal) {
        currentVal += (target - currentVal) * attackAlpha;
      } else {
        currentVal += (target - currentVal) * releaseAlpha;
      }
      smoothedEnvelope[i] = currentVal;
    }

    // 4. Map to Gain and schedule
    const duckedVolume = musicVolume * (1 - duckingAmount);
    const duckRange = musicVolume - duckedVolume;
    
    musicGain.gain.setValueAtTime(musicVolume, 0);
    
    for (let i = 0; i < numWindows; i++) {
      const time = startOffset + (i * windowDuration);
      // envelope is 1.0 when voice is present (ducked), 0.0 when silent (music loud)
      const targetGain = musicVolume - (smoothedEnvelope[i]! * duckRange);
      musicGain.gain.linearRampToValueAtTime(targetGain, time);
    }
  }
}
